import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowLeft, ClipboardList, FileText, FlaskConical, FolderOpen, HeartPulse, History,
  IdCard, Info, Pill, Users, Wallet,
} from 'lucide-react';
import { reportsAPI, prescriptionAPI, patientDocumentAPI } from '../../../services/api';
import { useToast } from '../../UIProvider';
import useReport from '../shared/useReport';
import useDrill from '../shared/useDrill';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import ReportPrintLayout from '../shared/ReportPrintLayout';
import PrintButton from '../shared/PrintButton';
import { getPresetRange } from '../shared/DateRangeFilter';
import { fmtMoney, fmtNum } from '../shared/chartTheme';
import DatePicker from '../../DatePicker';
import PrescriptionDetailModal from '../../PrescriptionDetailModal';
import { authedMediaUrl } from '../../../utils/media';
import { formatPatientAge } from '../../../utils/age';
import { clinicToday } from '../../../utils/clinicTime';
import { auditFieldLabel, formatAuditValue, vitalFieldLabel } from '../../../utils/visitAudit';
import { reportNoteCategory } from '../../../utils/reportNotes';
import { lookalikeRuleLabel, lookalikeRulesOf } from '../../../constants/lookalikeRules';
import { describeGuardian, guardianRelLabel } from '../../../constants/guardians';
import { formatBmi, vitalChipToneClass, vitalSeverityByLabel } from '../../../utils/vitals';
import {
  CANCEL_OUTCOME_LABEL, CREDIT_REASON_LABEL, DASH, DOC_TYPE_LABEL, INTAKE_ANSWER_LABEL, INTAKE_SECTION_LABEL,
  ITEM_TYPE_LABEL, LAB_URGENCY_LABEL, LOOKALIKE_KIND_LABEL, REFERRAL_SETTLEMENT_LABEL, StatusBadge, billPaymentLabel, col, dobAsOf, fmtDate,
  fmtDateTime, fmtSize, forPrint, human, tokenPaymentLabel,
} from './profileFormat';

// The second half of the Patient Profile: everything the hospital holds against one patient.
//
// The server decides what comes back. Without the Clinical reports permission there are no
// prescriptions, medicines, vitals or documents in the response at all; without Finance, no money.
// This screen only shows the sections that arrived, so the two can never disagree.

const PERIODS = [
  { key: 'all', label: 'All time' },
  { key: 'thisYear', label: 'This year' },
  { key: 'last6Months', label: 'Last 6 months' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

const has = (rows) => Array.isArray(rows) && rows.length > 0;
const anyOf = (rows, key) => (rows || []).some((r) => Number(r[key]) > 0);

// ---------------------------------------------------------------- small pieces

const Section = ({ title, subtitle, children }) => (
  <div className="space-y-3">
    {(title || subtitle) && (
      <div>
        {title && <h3 className="font-semibold text-gray-900">{title}</h3>}
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

const Banner = ({ tone = 'amber', children }) => {
  const cls = {
    amber: 'bg-amber-50 border-amber-500 text-amber-900',
    blue: 'bg-sky-50 border-sky-500 text-sky-900',
    grey: 'bg-gray-50 border-gray-400 text-gray-700',
  }[tone];
  return (
    <div className={`border-l-4 p-4 rounded-lg text-sm flex items-start gap-2 ${cls}`}>
      {tone === 'amber' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />}
      <div>{children}</div>
    </div>
  );
};

/** Label / amount rows ending in a bold total: how a money figure is made up. */
const Breakdown = ({ title, rows, total, notes = [] }) => (
  <div className="bg-white rounded-2xl shadow p-5">
    <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
    <table className="w-full text-sm">
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.label}>
            <td className="py-2 pr-3 text-gray-600">
              {r.label}
              {r.sub && <div className="text-xs text-gray-400">{r.sub}</div>}
            </td>
            <td className={`py-2 text-right whitespace-nowrap ${r.minus ? 'text-rose-700' : 'text-gray-900'}`}>
              {r.minus ? `- ${fmtMoney(r.value)}` : fmtMoney(r.value)}
            </td>
          </tr>
        ))}
        <tr className="border-t-2 border-gray-300">
          <td className="pt-3 pr-3 font-semibold text-gray-900">{total.label}</td>
          <td className="pt-3 text-right font-bold text-gray-900 whitespace-nowrap">{fmtMoney(total.value)}</td>
        </tr>
      </tbody>
    </table>
    {notes.filter(Boolean).length > 0 && (
      <ul className="mt-3 space-y-1 text-xs text-gray-500 list-disc pl-4">
        {notes.filter(Boolean).map((n) => <li key={n}>{n}</li>)}
      </ul>
    )}
  </div>
);

/** The two money cards, and the same figures as rows for the printout. */
const moneyBreakdown = (money, allTime) => {
  if (!money) return null;
  const t = money.tokens;
  const b = money.bills;
  const paidRows = [
    { label: 'Consultation fees paid at the desk', value: t.collected, sub: `${t.visits} visit${t.visits === 1 ? '' : 's'}${t.cancelled ? `, plus ${t.cancelled} cancelled` : ''}` },
    ...(Number(t.refunded) > 0 ? [{ label: 'Refunded to the patient', value: t.refunded, minus: true }] : []),
    { label: 'Bills paid (medicines, services, supplies)', value: b.paid, sub: `${b.paidCount} completed bill${b.paidCount === 1 ? '' : 's'}` },
  ];
  const paidNotes = [
    Number(t.creditUsed) > 0 && `${fmtMoney(t.creditUsed)} of consultation fees came out of the patient's own credit and is not counted again.`,
    Number(t.keptOnCancelled) > 0 && `${fmtMoney(t.keptOnCancelled)} was paid for tokens later cancelled and kept by the hospital.`,
    Number(t.creditedOnCancelled) > 0 && `${fmtMoney(t.creditedOnCancelled)} was paid for tokens later cancelled and held as credit.`,
    (Number(t.keptOnCancelled) > 0 || Number(t.creditedOnCancelled) > 0)
      && 'Cancelled tokens count here when their fee was kept or held as credit, so this can be higher than the Top Payers report, which leaves cancelled tokens out.',
    Number(b.openCount) > 0 && `${b.openCount} bill${b.openCount === 1 ? ' was' : 's were'} never completed (${fmtMoney(b.openValue)}) and ${b.openCount === 1 ? 'is' : 'are'} not counted.`,
    allTime && Number(money.creditBalance) > 0 && `${fmtMoney(money.creditBalance)} of it is still held as credit, so ${fmtMoney(money.spentOnCare)} has been spent on care.`,
  ];
  const givenRows = [
    { label: 'Discount on consultation fees', value: t.discount, sub: Number(t.freeTokens) > 0 ? `${t.freeTokens} consultation${t.freeTokens === 1 ? '' : 's'} given free` : undefined },
    { label: 'Discount on bill items', value: b.lineDiscount },
    ...(Number(b.billDiscount) > 0 ? [{ label: 'Discount on whole bills', value: b.billDiscount }] : []),
  ];
  const givenNotes = [
    `Full price of the care received: ${fmtMoney(money.fullPrice)} (consultations at their listed fee plus bill items before discount).`,
    Number(b.waivedInFull) > 0 && `${b.waivedInFull} bill${b.waivedInFull === 1 ? ' was' : 's were'} waived in full.`,
    Number(b.roundedInFavour) > 0 && `A further ${fmtMoney(b.roundedInFavour)} was rounded off bills in the patient's favour. Receipts show it as Round off, not as a discount, so it is not counted above.`,
  ];
  return {
    paid: { rows: paidRows, total: { label: 'Total paid by the patient', value: money.totalPaid }, notes: paidNotes },
    given: { rows: givenRows, total: { label: 'Total given by the hospital', value: money.subsidy }, notes: givenNotes },
  };
};

// ---------------------------------------------------------------- columns

const visitColumns = (visits, money) => {
  const cols = [
    col('visitDate', 'Date', 'date'),
    col('token', 'Token', 'mono'),
    col('doctor', 'Doctor', 'text'),
    col('service', 'Service', 'wrap'),
    {
      key: 'status',
      label: 'Status',
      render: (r) => (
        <span className="inline-flex flex-wrap gap-1">
          <StatusBadge value={r.status} />
          {r.emergency && <StatusBadge label="Emergency" tone="red" />}
          {r.revisit && <StatusBadge label="Revisit" tone="blue" />}
          {r.referredFromToken && <StatusBadge label="Referred in" tone="violet" />}
        </span>
      ),
      csv: (r) => human(r.status),
      print: (r) => [human(r.status), r.emergency && 'Emergency', r.revisit && 'Revisit', r.referredFromToken && 'Referred in'].filter(Boolean).join(', '),
    },
  ];
  if (money) {
    cols.push(col('fee', 'Fee', 'money'), col('discount', 'Discount', 'money'));
    if (anyOf(visits, 'creditUsed')) cols.push(col('creditUsed', 'From Credit', 'money'));
    cols.push(col('paid', 'Paid', 'money'));
    if (anyOf(visits, 'refunded')) cols.push(col('refunded', 'Refunded', 'money'));
    cols.push(col((r) => tokenPaymentLabel(r), 'Payment', 'text'));
  }
  // Columns that are empty for every visit of this patient are left out, so the ones that matter
  // are not squeezed into a table wider than the page.
  if ((visits || []).some((r) => visitDetails(r, money))) {
    cols.push({
      key: 'details',
      label: 'Details',
      wrap: true,
      render: (r) => visitDetails(r, money) || DASH,
      csv: (r) => visitDetails(r, money),
      print: (r) => visitDetails(r, money) || DASH,
    });
  }
  cols.push(col('prescriptionNumber', 'Rx', 'mono'));
  if ((visits || []).some((r) => r.broughtBy)) {
    cols.push(
      col((r) => describeGuardian({ name: r.broughtBy, relationship: r.broughtByRelationship, relationshipOther: r.broughtByRelationshipOther }) || null, 'Brought By', 'wrap'),
    );
  }
  return cols;
};

/** One sentence per visit saying what was unusual about it: cancelled, referred, a revisit. */
const visitDetails = (r, money) => {
  const parts = [];
  if (r.status === 'CANCELLED') {
    if (money && r.cancelOutcome) parts.push(CANCEL_OUTCOME_LABEL[r.cancelOutcome]);
    if (money && Number(r.creditReturned) > 0) parts.push(`${fmtMoney(r.creditReturned)} credit returned`);
    if (r.notes) parts.push(`Reason: ${r.notes}`);
  } else if (r.notes) {
    parts.push(r.notes);
  }
  if (r.referredTo) {
    parts.push(`Referred to ${r.referredTo}${r.referralReason ? `: ${r.referralReason}` : ''}`);
    if (r.referralSettlement) parts.push(REFERRAL_SETTLEMENT_LABEL[r.referralSettlement] || human(r.referralSettlement));
  }
  if (r.referredFromToken) parts.push(`Referred from token ${r.referredFromToken} (${fmtDate(r.referredFromDate)})`);
  if (r.revisitReason) parts.push(`Revisit: ${r.revisitReason}`);
  if (r.emergencyReason) parts.push(`Emergency: ${r.emergencyReason}`);
  if (money && r.collectedBy) parts.push(`Collected by ${r.collectedBy}`);
  return parts.join('. ');
};

const prescriptionColumns = () => [
  col('date', 'Date', 'date'),
  col('number', 'Rx', 'mono'),
  col('token', 'Token', 'mono'),
  col('doctor', 'Prescribed By', 'wrap'),
  {
    key: 'diagnosis',
    label: 'Diagnosis',
    wrap: true,
    render: (r) => (
      <div>
        <div>{r.diagnosis || (r.consultationOnly ? 'Consultation only' : DASH)}</div>
        {r.diagnosisUrdu && (
          <div className="urdu-text text-gray-600" dir="rtl" style={{ lineHeight: 2.2 }}>{r.diagnosisUrdu}</div>
        )}
      </div>
    ),
    csv: (r) => r.diagnosis || '',
    print: (r) => r.diagnosis || (r.consultationOnly ? 'Consultation only' : DASH),
  },
  col('complaints', 'Complaints', 'wrap'),
  col('medicineCount', 'Medicines', 'number'),
  col('labTestCount', 'Lab Tests', 'number'),
  col((r) => (r.followupDays ? `${r.followupDays} days` : null), 'Follow-up', 'text'),
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <span className="inline-flex flex-wrap gap-1">
        <StatusBadge value={r.status} />
        {r.hasPrivateNotes && <StatusBadge label="Private notes" tone="grey" />}
        {r.bedsideSugar && <StatusBadge label="Sugar checked" tone="blue" />}
      </span>
    ),
    csv: (r) => human(r.status),
    print: (r) => human(r.status),
  },
];

const medicineSummaryColumns = (money) => [
  col((r) => [r.name, r.strength].filter(Boolean).join(' '), 'Medicine', 'wrap'),
  col((r) => (r.supply ? 'Supply' : r.fromInventory ? 'Hospital stock' : 'Bought outside'), 'Source', 'text'),
  col('timesPrescribed', 'Times Prescribed', 'number'),
  col('quantityPrescribed', 'Qty Prescribed', 'number'),
  col('quantityBilled', 'Qty Given', 'number'),
  // Line totals as the receipt prints them, before any round-off on the whole bill: "Billed", not
  // "Paid", so a column added up by hand is not taken for the cash received.
  ...(money ? [col('amountPaid', 'Billed', 'money'), col('discount', 'Discount', 'money')] : []),
  col('firstPrescribed', 'First', 'date'),
  col('lastPrescribed', 'Last', 'date'),
];

/**
 * "5 days", or the unit alone for one with no count ("Until Finished"). A continuous course stores
 * the days of supply given in its count, so it reads "Continuous, 15 days' supply".
 */
const durationText = (r) => {
  if (!r.durationUnit) return r.durationValue ? String(r.durationValue) : null;
  const unit = human(r.durationUnit);
  if (r.durationUnit === 'CONTINUOUS') return r.durationValue ? `Continuous, ${r.durationValue} days' supply` : unit;
  return r.durationValue ? `${r.durationValue} ${unit.toLowerCase()}` : unit;
};

const medicineLineColumns = (money) => [
  col('date', 'Date', 'date'),
  col('prescriptionNumber', 'Rx', 'mono'),
  col('doctor', 'Prescribed By', 'wrap'),
  col((r) => [r.name, r.strength].filter(Boolean).join(' '), 'Medicine', 'wrap'),
  col('dosage', 'Dosage', 'wrap'),
  col((r) => durationText(r), 'Duration', 'text'),
  col('quantityPrescribed', 'Qty Prescribed', 'number'),
  col('quantityBilled', 'Qty Given', 'number'),
  ...(money ? [col('amountBilled', 'Billed', 'money')] : []),
  col((r) => (r.fromInventory ? 'Hospital stock' : 'Bought outside'), 'Source', 'text'),
];

const labColumns = () => [
  col('date', 'Date', 'date'),
  col('prescriptionNumber', 'Rx', 'mono'),
  col('doctor', 'Ordered By', 'wrap'),
  col('name', 'Test', 'wrap'),
  col('urgency', 'Urgency', 'text', { text: (r) => LAB_URGENCY_LABEL[r.urgency] || human(r.urgency) }),
  col('instructions', 'Instructions', 'wrap'),
  col((r) => (Number(r.results) > 0 ? `Report uploaded ${fmtDate(r.resultDate)}` : 'No report uploaded'), 'Result', 'text'),
];

const vitalColumns = (vitals, dateOfBirth) => {
  const chip = (label, text) => (r) => {
    if (text(r) === null) return DASH;
    const severity = vitalSeverityByLabel(r, dobAsOf(dateOfBirth, r.date))(label);
    if (severity === 'normal') return text(r);
    return <span className={`px-1.5 py-0.5 rounded border text-xs font-medium ${vitalChipToneClass(severity)}`}>{text(r)}</span>;
  };
  const v = (key, unit = '') => (r) => (r[key] === null || r[key] === undefined ? null : `${r[key]}${unit}`);
  const bp = (r) => (r.bpSystolic && r.bpDiastolic ? `${r.bpSystolic}/${r.bpDiastolic}` : null);
  const reading = (key, label, text) => ({ key, label, align: 'right', render: chip(label, text), csv: (r) => text(r) ?? '', print: (r) => text(r) ?? DASH });
  const rows = vitals || [];
  const cols = [col('date', 'Date', 'date'), col('token', 'Token', 'mono')];
  // A reading the nurse never takes for this patient gets no column: an adult has no MUAC, and a
  // clinic that skips SpO2 would otherwise show a column of dashes.
  const readings = [
    ['bp', 'BP', bp],
    ['pulse', 'Pulse', v('pulse')],
    ['temperature', 'Temp', v('temperature', ' °F')],
    ['spo2', 'SpO₂', v('spo2', '%')],
    ['respiratoryRate', 'Resp Rate', v('respiratoryRate')],
    ['bloodSugar', 'Blood Sugar', v('bloodSugar')],
    ['weight', 'Weight', v('weight', ' kg')],
    ['height', 'Height', v('height', ' cm')],
    ['lengthCm', 'Length', v('lengthCm', ' cm')],
    ['headCircumferenceCm', 'Head', v('headCircumferenceCm', ' cm')],
    ['muacCm', 'MUAC', v('muacCm', ' cm')],
  ];
  readings.forEach(([key, label, text]) => {
    if (rows.some((r) => text(r) !== null)) cols.push(reading(key, label, text));
  });
  if (rows.some((r) => formatBmi(r))) {
    cols.push({ key: 'bmi', label: 'BMI', align: 'right', render: (r) => formatBmi(r) || DASH, csv: (r) => formatBmi(r) || '', print: (r) => formatBmi(r) || DASH });
  }
  cols.push(
    col('recordedBy', 'Recorded By', 'wrap'),
    col((r) => (r.edited ? 'Amended' : null), 'Edited', 'text'),
  );
  return cols;
};

const invoiceColumns = (invoices) => {
  const rows = invoices || [];
  const cols = [
    // A bill never completed has no completion date; its status says why, and it is dated by the
    // day it was raised only for sorting.
    col('date', 'Completed', 'date', { text: (r) => (r.completedAt ? fmtDate(r.completedAt) : DASH) }),
    col('number', 'Invoice', 'mono'),
    col('prescriptionNumber', 'Rx', 'mono'),
    col('status', 'Status', 'status'),
    col('gross', 'Before Discount', 'money'),
    col((r) => Number(r.lineDiscount || 0) + Number(r.billDiscount || 0), 'Discount', 'money'),
  ];
  if (rows.some((r) => Number(r.rounding) !== 0)) cols.push(col('rounding', 'Round Off', 'signed'));
  cols.push(
    col('amountPaid', 'Paid', 'money'),
    col((r) => [billPaymentLabel(r), r.collectedBy && `collected by ${r.collectedBy}`].filter((x) => x && x !== DASH).join(', ') || null, 'Payment', 'wrap'),
  );
  if (rows.some((r) => r.cancellationReason)) {
    cols.push(col((r) => (r.cancellationReason ? `Cancelled: ${r.cancellationReason}` : null), 'Notes', 'wrap'));
  }
  return cols;
};

const billedItemColumns = () => [
  col('date', 'Date', 'date'),
  col('invoiceNumber', 'Invoice', 'mono'),
  col((r) => [r.name, r.description].filter(Boolean).join(' · '), 'Item', 'wrap'),
  col((r) => (r.supply ? 'Supply' : ITEM_TYPE_LABEL[r.itemType] || human(r.itemType)), 'Type', 'text'),
  col('quantity', 'Qty', 'number'),
  col('unitPrice', 'Unit Price', 'money'),
  col('gross', 'Before Discount', 'money'),
  col('discount', 'Discount', 'money'),
  col('total', 'Total', 'money'),
  col('invoiceStatus', 'Bill', 'status'),
];

const creditColumns = () => [
  col('at', 'Date', 'datetime'),
  col((r) => CREDIT_REASON_LABEL[r.reason] || human(r.reason), 'Entry', 'text'),
  col('amount', 'Amount', 'signed'),
  col('balance', 'Balance', 'money'),
  col((r) => [r.fromToken && `from token ${r.fromToken}`, r.toToken && `on token ${r.toToken}`].filter(Boolean).join(', ') || null, 'Token', 'text'),
  col('notes', 'Notes', 'wrap'),
  col('performedBy', 'By', 'text'),
];

const alertColumns = () => [
  col('severity', 'Severity', 'status'),
  col((r) => r.typeLabel || human(r.type), 'Type', 'text'),
  col('title', 'Alert', 'wrap'),
  col((r) => [r.description, r.notes].filter(Boolean).join('. ') || null, 'Details', 'wrap'),
  col('diagnosedDate', 'Since', 'date'),
  col('createdBy', 'Added By', 'text'),
  col('createdAt', 'Added On', 'date'),
];

const intakeColumns = () => [
  col((r) => INTAKE_SECTION_LABEL[r.section] || human(r.section), 'Section', 'text'),
  col('labelEnglish', 'Question', 'wrap'),
  col((r) => INTAKE_ANSWER_LABEL[r.answer] || r.answer || null, 'Answer', 'text'),
  col('detail', 'Detail', 'wrap'),
  col((r) => (r.serious && r.answer === 'YES' ? 'Significant' : null), 'Flag', 'text'),
];

const reportNoteColumns = () => [
  col('date', 'Date', 'date'),
  col('category', 'Category', 'text', { text: (r) => reportNoteCategory(r.category).label }),
  col('text', 'Note', 'wrap'),
  col('author', 'Author', 'text'),
  col('prescriptionNumber', 'Rx', 'mono'),
];

const familyColumns = () => [
  col('name', 'Name', 'wrap'),
  col('mrn', 'MRN', 'mono'),
  col((r) => (r.head ? 'Family head' : 'Family member'), 'In the Family', 'text'),
  col((r) => `${formatPatientAge(r) || DASH} · ${human(r.gender)}`, 'Age / Gender', 'text'),
  col('phone', 'Phone', 'text'),
  col('status', 'Status', 'status'),
];

const guardianColumns = () => [
  col('name', 'Name', 'wrap'),
  col((r) => guardianRelLabel(r.relationship, r.relationshipOther) || null, 'Relationship', 'text'),
  col('cnic', 'CNIC', 'mono'),
  col('phone', 'Phone', 'text'),
  col((r) => (r.primary ? 'Primary' : null), 'Primary', 'text'),
  col('linkedMrn', 'Own File', 'mono'),
  col('addedBy', 'Added By', 'text'),
  col('addedAt', 'Added On', 'date'),
];

const documentColumns = (patientId) => [
  col((r) => r.documentDate || r.uploadedAt, 'Date', 'date'),
  col('type', 'Type', 'text', { text: (r) => DOC_TYPE_LABEL[r.type] || human(r.type) }),
  {
    key: 'fileName',
    label: 'File',
    wrap: true,
    render: (r) =>
      r.active ? (
        <a
          href={authedMediaUrl(patientDocumentAPI.getDownloadUrl(patientId, r.id))}
          target="_blank"
          rel="noreferrer"
          className="text-emerald-700 hover:underline"
        >
          {r.fileName}
        </a>
      ) : (
        <span className="text-gray-400 line-through">{r.fileName}</span>
      ),
    csv: (r) => r.fileName,
    print: (r) => r.fileName,
  },
  col('description', 'Description', 'wrap'),
  col('prescriptionNumber', 'Rx', 'mono'),
  col('uploadedBy', 'Uploaded By', 'text'),
  col('fileSize', 'Size', 'text', { text: (r) => fmtSize(r.fileSize) }),
  col((r) => (r.active ? 'On file' : `Removed${r.removedBy ? ` by ${r.removedBy}` : ''}${r.removedAt ? ` on ${fmtDate(r.removedAt)}` : ''}`), 'State', 'text'),
];

const aliasColumns = () => [
  col('mrn', 'Old MRN', 'mono'),
  col('name', 'Name on That Record', 'wrap'),
  col('mergedInto', 'Merged Into', 'mono'),
  col('mergedAt', 'Merged On', 'datetime'),
  col('mergedBy', 'By', 'text'),
  col((r) => `${r.visitsMoved} visits, ${r.prescriptionsMoved} prescriptions, ${r.invoicesMoved} bills, ${r.documentsMoved} documents`, 'Moved Across', 'wrap'),
  col('note', 'Note', 'wrap'),
];

const auditColumns = () => [
  col('at', 'When', 'datetime'),
  col('token', 'Token', 'mono'),
  col('visitDate', 'Visit', 'date'),
  col((r) => (r.action === 'VITALS_EDITED' ? `Vitals: ${vitalFieldLabel(r.field)}` : auditFieldLabel(r.field)), 'Changed', 'text'),
  col((r) => formatAuditValue(r.oldValue, r.field), 'From', 'text'),
  col((r) => formatAuditValue(r.newValue, r.field), 'To', 'text'),
  col('by', 'By', 'text'),
  col('reason', 'Reason', 'wrap'),
];

const lookalikeColumns = () => [
  col('decidedAt', 'Decided On', 'datetime'),
  col((r) => LOOKALIKE_KIND_LABEL[r.kind] || human(r.kind), 'Decision', 'wrap'),
  col((r) => (r.otherName ? `${r.otherName} (${r.otherMrn})` : r.otherMrn || 'Record no longer exists'), 'Other Record', 'wrap'),
  col('strength', 'Match', 'status'),
  col('rules', 'Why It Looked Alike', 'wrap', { text: (r) => lookalikeRulesOf(r).map(lookalikeRuleLabel).join(', ') || DASH }),
  col('decidedBy', 'By', 'text'),
  col('note', 'Note', 'wrap'),
];

// ---------------------------------------------------------------- identity

/** Every identity field worth showing, as label/value pairs; blanks are dropped. */
const identityFields = (p) => {
  const address = [p.area, p.town, p.city].filter(Boolean).join(', ');
  const status = p.status && p.status !== 'ACTIVE'
    ? `${human(p.status)}${p.statusChangedAt ? ` since ${fmtDate(p.statusChangedAt)}` : ''}${p.statusReason ? `: ${p.statusReason}` : ''}`
    : 'Active';
  const legacyGuardian = p.guardianName
    ? describeGuardian({ name: p.guardianName, relationship: p.guardianRelationship, relationshipOther: p.guardianRelationshipOther })
      + (p.guardianCnic ? `, ${p.guardianCnic}` : '')
    : null;
  return [
    ['Name', `${p.firstName || ''} ${p.lastName || ''}`.trim()],
    ['MRN', p.mrn],
    ['Gender', human(p.gender)],
    ['Date of Birth', p.dateOfBirth ? `${fmtDate(p.dateOfBirth)}${p.dobEstimated ? ' (estimated)' : ''}` : null],
    ['Age', formatPatientAge(p)],
    ['Marital Status', p.maritalStatus ? human(p.maritalStatus) : null],
    ['Phone', p.phone],
    ['CNIC', p.cnic],
    ['B-Form', p.bFormNumber],
    ["Father's Name", p.fatherName],
    ["Father's CNIC", p.fatherCnic],
    ["Mother's Name", p.motherName],
    ["Mother's CNIC", p.motherCnic],
    ["Husband's Name", p.husbandName],
    ['Guardian on File', legacyGuardian],
    ['Address', address || null],
    ['Family Head', p.familyHeadMrn ? `${p.familyHeadName} (${p.familyHeadMrn})` : null],
    ['Registered', fmtDateTime(p.registeredAt)],
    ['Status', status],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== DASH);
};

const Avatar = ({ patient }) => {
  const [failed, setFailed] = useState(false);
  const initials = `${(patient.firstName || '?')[0] || ''}${(patient.lastName || '')[0] || ''}`.toUpperCase();
  if (patient.photoUrl && !failed) {
    return (
      <img
        src={authedMediaUrl(patient.photoUrl)}
        alt=""
        onError={() => setFailed(true)}
        className="w-20 h-20 rounded-2xl object-cover border border-gray-200 flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-20 h-20 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-2xl font-bold flex-shrink-0">
      {initials}
    </div>
  );
};

// ---------------------------------------------------------------- the view

// initialPeriod ({ from, to }) opens the profile on a period rather than all time, as the Frequent
// Patients report does with its own range; backLabel names where Back returns to.
const PatientProfileView = ({ patientId, onBack, onOpenPatient, initialPeriod, backLabel = 'Back to results' }) => {
  const notify = useToast();
  const drill = useDrill();
  const tabBarRef = useRef(null);
  const [period, setPeriod] = useState(() =>
    initialPeriod && (initialPeriod.from || initialPeriod.to)
      ? { key: 'custom', from: initialPeriod.from || '', to: initialPeriod.to || '' }
      : { key: 'all', from: '', to: '' }
  );
  const [tab, setTab] = useState('overview');
  const [rx, setRx] = useState(null);
  const [rxLoading, setRxLoading] = useState(null);
  const today = clinicToday();

  const fetcher = useCallback(
    () => reportsAPI.getPatientProfile({ patientId, from: period.from || undefined, to: period.to || undefined }),
    [patientId, period.from, period.to]
  );
  const { data, loading } = useReport(fetcher);

  const choosePeriod = (key) => {
    if (key === 'all') setPeriod({ key, from: '', to: '' });
    else if (key === 'custom') setPeriod((p) => ({ ...p, key }));
    else setPeriod({ key, ...getPresetRange(key) });
  };

  const openTab = (id) => {
    setTab(id);
    // Bring the new section's top into view when the tab bar has scrolled off the screen; left
    // where it was otherwise, so a short click does not jump the page.
    const bar = tabBarRef.current;
    if (bar && bar.getBoundingClientRect().top < 0) bar.scrollIntoView({ block: 'start' });
  };

  const openPrescription = async (row) => {
    if (!row?.id || rxLoading) return;
    setRxLoading(row.id);
    try {
      setRx(await prescriptionAPI.getById(row.id));
    } catch (e) {
      notify('error', e.message || 'The prescription could not be opened');
    } finally {
      setRxLoading(null);
    }
  };

  const access = data?.access || {};
  const clinical = Boolean(access.clinical);
  // A visit opens the prescription written on it, for readers who may see prescriptions.
  const openVisitPrescription = clinical ? (r) => r.prescriptionId && openPrescription({ id: r.prescriptionId }) : undefined;
  const money = Boolean(access.money);
  const patient = data?.patient;
  const summary = useMemo(() => data?.summary || {}, [data]);
  const m = data?.money;
  const allTime = data?.period?.allTime !== false;
  const breakdown = useMemo(() => moneyBreakdown(m, allTime), [m, allTime]);

  const visits = useMemo(() => data?.visits || [], [data]);
  const cols = useMemo(
    () => ({
      visits: visitColumns(visits, money),
      prescriptions: prescriptionColumns(),
      medicineSummary: medicineSummaryColumns(money),
      medicineLines: medicineLineColumns(money),
      labs: labColumns(),
      vitals: vitalColumns(data?.vitals, patient?.dateOfBirth),
      invoices: invoiceColumns(data?.invoices),
      billedItems: billedItemColumns(),
      credits: creditColumns(),
      alerts: alertColumns(),
      intake: intakeColumns(),
      reportNotes: reportNoteColumns(),
      family: familyColumns(),
      guardians: guardianColumns(),
      documents: documentColumns(patientId),
      aliases: aliasColumns(),
      audit: auditColumns(),
      lookalikes: lookalikeColumns(),
    }),
    [visits, money, data?.vitals, data?.invoices, patient?.dateOfBirth, patientId]
  );

  const kpis = useMemo(() => {
    const items = [
      { label: 'Visits', value: fmtNum(summary.visits ?? 0), sub: summary.cancelledVisits ? `${summary.cancelledVisits} cancelled` : 'None cancelled' },
      { label: 'Last Visit', value: fmtDate(summary.lastVisit), sub: summary.firstVisit ? `First ${fmtDate(summary.firstVisit)}` : 'No visits' },
    ];
    const doctors = { label: 'Doctors Seen', value: fmtNum(summary.doctorsSeen ?? 0), sub: 'Different doctors' };
    const family = { label: 'Family Members', value: fmtNum((data?.family || []).length), sub: 'Linked in the family' };
    const guardians = { label: 'Guardians', value: fmtNum((data?.guardians || []).length), sub: 'On file' };
    const emergencies = { label: 'Emergencies', value: fmtNum(summary.emergencies ?? 0), sub: 'Visits marked emergency' };
    const alerts = { label: 'Medical Alerts', value: fmtNum(summary.alerts ?? 0), sub: 'On file', tone: summary.alerts ? 'alert' : undefined };
    items.push(clinical ? { label: 'Prescriptions', value: fmtNum(summary.prescriptions ?? 0), sub: `${summary.labTests ?? 0} lab tests ordered` } : doctors);
    if (money && m) {
      items.push(
        { label: 'Total Paid', value: fmtMoney(m.totalPaid), sub: 'Consultations and bills' },
        { label: 'Given by Hospital', value: fmtMoney(m.subsidy), sub: 'Discounts on fees and bills' },
        { label: 'Credit Balance', value: fmtMoney(m.creditBalance), sub: Number(m.creditBalance) > 0 ? 'Held for the patient' : 'Nothing held' },
      );
    } else if (clinical) {
      items.push(doctors, alerts, family);
    } else {
      items.push(emergencies, family, guardians);
    }
    return items;
  }, [summary, clinical, money, m, data?.family, data?.guardians]);

  const tabs = useMemo(() => {
    if (!data) return [];
    const count = (...lists) => lists.reduce((n, l) => n + (l ? l.length : 0), 0);
    return [
      { id: 'overview', label: 'Overview', icon: IdCard },
      { id: 'visits', label: 'Visits', icon: Activity, count: visits.length },
      clinical && { id: 'prescriptions', label: 'Prescriptions', icon: FileText, count: count(data.prescriptions) },
      clinical && { id: 'medicines', label: 'Medicines', icon: Pill, count: count(data.medicineSummary) },
      clinical && { id: 'labs', label: 'Lab Tests', icon: FlaskConical, count: count(data.labTests) },
      clinical && { id: 'vitals', label: 'Vitals', icon: HeartPulse, count: count(data.vitals) },
      money && { id: 'money', label: 'Money', icon: Wallet, count: count(data.invoices) },
      clinical && { id: 'history', label: 'Medical History', icon: ClipboardList, count: count(data.alerts, data.intake?.answers, data.reportNotes) },
      { id: 'family', label: 'Family & Guardians', icon: Users, count: count(data.family, data.guardians) },
      clinical && { id: 'documents', label: 'Documents', icon: FolderOpen, count: count(data.documents) },
      { id: 'record', label: 'Record History', icon: History, count: count(data.aliases, data.auditTrail, data.lookalikes) },
    ].filter(Boolean);
  }, [data, clinical, money, visits.length]);

  const activeTab = tabs.some((t) => t.id === tab) ? tab : 'overview';

  // Who has brought this patient in, from the per-visit record: most frequent first.
  const broughtBy = useMemo(() => {
    const byName = new Map();
    visits.forEach((v) => {
      if (!v.broughtBy) return;
      const label = describeGuardian({ name: v.broughtBy, relationship: v.broughtByRelationship, relationshipOther: v.broughtByRelationshipOther });
      const entry = byName.get(label) || { label, times: 0, last: null };
      entry.times += 1;
      if (!entry.last || v.visitDate > entry.last) entry.last = v.visitDate;
      byName.set(label, entry);
    });
    return [...byName.values()].sort((a, b) => b.times - a.times || a.label.localeCompare(b.label));
  }, [visits]);

  const printTables = useMemo(() => {
    if (!data || !patient) return [];
    const t = [];
    const add = (title, columns, rows) => { if (has(rows)) t.push({ title, columns: forPrint(columns), rows }); };
    t.push({
      title: 'Patient details',
      columns: [{ key: 'label', label: 'Detail' }, { key: 'value', label: 'Value' }],
      rows: identityFields(patient).map(([label, value]) => ({ label, value })),
    });
    if (breakdown) {
      // The notes print too: without them a printed total loses the caveats the screen gives it
      // (cancelled fees counted, credit not counted twice, bills never completed).
      const moneyRows = (b) => [
        ...b.rows.map((r) => ({
          label: r.sub ? `${r.label} (${r.sub})` : r.label,
          value: r.minus ? `- ${fmtMoney(r.value)}` : fmtMoney(r.value),
        })),
        { label: b.total.label, value: fmtMoney(b.total.value) },
        ...b.notes.filter(Boolean).map((n) => ({ label: n, value: '' })),
      ];
      const moneyCols = [{ key: 'label', label: 'Item' }, { key: 'value', label: 'Amount', align: 'right' }];
      t.push({ title: 'Paid by the patient', columns: moneyCols, rows: moneyRows(breakdown.paid) });
      t.push({ title: 'Given by the hospital', columns: moneyCols, rows: moneyRows(breakdown.given) });
    }
    add('Visits', cols.visits, visits);
    add('Prescriptions', cols.prescriptions, data.prescriptions);
    add('Medicines', cols.medicineSummary, data.medicineSummary);
    add('Every prescribed medicine', cols.medicineLines, data.medicines);
    add('Lab tests', cols.labs, data.labTests);
    add('Vitals', cols.vitals, data.vitals);
    add('Bills', cols.invoices, data.invoices);
    add('Items billed', cols.billedItems, data.billedItems);
    add('Credit ledger', cols.credits, data.credits);
    add('Medical alerts', cols.alerts, data.alerts);
    add('Medical history (first visit)', cols.intake, data.intake?.answers);
    add('Report notes', cols.reportNotes, data.reportNotes);
    add('Family', cols.family, data.family);
    add('Guardians', cols.guardians, data.guardians);
    add('Documents', cols.documents, data.documents);
    add('Also known as', cols.aliases, data.aliases);
    add('Token edits', cols.audit, data.auditTrail);
    add('Look-alike decisions', cols.lookalikes, data.lookalikes);
    return t;
  }, [data, patient, breakdown, cols, visits]);

  // ---- render

  if (!data) {
    return (
      <div className="space-y-5">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>
        <div className="bg-white rounded-2xl shadow p-12 text-center text-sm text-gray-500">
          {loading ? 'Loading the patient profile' : 'The patient profile could not be loaded.'}
        </div>
      </div>
    );
  }

  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
  const periodText = allTime
    ? 'All time'
    : `${data.period.from ? fmtDate(data.period.from) : 'the start'} to ${data.period.to ? fmtDate(data.period.to) : 'today'}`;
  const fields = identityFields(patient);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </button>
          <div className="ml-auto flex items-center gap-2">
            {loading && <span className="text-xs text-gray-400">Updating</span>}
            <PrintButton />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Avatar patient={patient} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">{name}</h2>
              <span className="font-mono text-sm bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{patient.mrn}</span>
              <StatusBadge value={patient.status} />
              {patient.archived && <StatusBadge label="Archived" tone="grey" />}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {[formatPatientAge(patient), human(patient.gender)].filter((x) => x && x !== DASH).join(' · ')}
            </p>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {fields
                // Name, MRN, age, gender and an Active status are already in the heading above.
                .filter(([label, value]) => !['Name', 'MRN', 'Age', 'Gender'].includes(label) && !(label === 'Status' && value === 'Active'))
                .map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-xs text-gray-500">{label}</dt>
                    <dd className="text-gray-900 break-words">{value}</dd>
                  </div>
                ))}
            </dl>
          </div>
        </div>
      </div>

      {data.openedFromMergedRecord && (
        <Banner tone="blue">The record you opened was merged into this one, so its visits, prescriptions and bills are shown here.</Banner>
      )}
      {patient.archived && (
        <Banner>
          This record is archived{patient.archivedBy ? ` by ${patient.archivedBy}` : ''}
          {patient.archivedAt ? ` on ${fmtDate(patient.archivedAt)}` : ''}. It no longer appears in the patient list.
        </Banner>
      )}
      {clinical && has(data.alerts) && (
        <Banner>
          <span className="font-semibold">Medical alerts: </span>
          {data.alerts.map((a) => a.title || a.typeLabel).join(', ')}
        </Banner>
      )}

      {/* Period */}
      <div className="bg-white rounded-2xl shadow p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-1">Period</span>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => choosePeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period.key === p.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          {period.key === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker value={period.from} onChange={(v) => setPeriod((p) => ({ ...p, from: v }))} max={period.to || today} placeholder="From" />
              <span className="text-gray-400 text-sm">to</span>
              <DatePicker value={period.to} onChange={(v) => setPeriod((p) => ({ ...p, to: v }))} min={period.from || undefined} max={today} placeholder="To" />
            </div>
          )}
        </div>
        {!allTime && (
          <p className="text-xs text-gray-500">
            Visits, prescriptions and bills from {periodText}. Alerts, family, guardians, documents, the medical history and the credit
            balance are shown in full whatever the period.
          </p>
        )}
      </div>

      <KpiStrip items={kpis} loading={loading} />

      {/* Section tabs */}
      <div ref={tabBarRef} className="bg-white rounded-2xl shadow p-2 flex flex-wrap gap-1 scroll-mt-4">
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => openTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                on ? 'bg-navy-800 text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {t.count !== undefined && (
                <span className={`text-xs rounded-full px-1.5 ${on ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          {breakdown ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Breakdown title="Paid by the patient" {...breakdown.paid} />
              <Breakdown title="Given by the hospital" {...breakdown.given} />
            </div>
          ) : (
            <Banner tone="grey">Money is shown to users with the Finance reports permission.</Banner>
          )}
          <ReportTable
            title="Recent visits"
            columns={cols.visits}
            rows={visits.slice(0, 5)}
            onRowClick={openVisitPrescription}
            searchable={false}
            emptyText={`No visits in ${allTime ? 'this record' : 'this period'}`}
            footnote={visits.length > 5 ? `The latest 5 of ${visits.length} visits. The Visits tab lists them all.` : undefined}
          />
          {!clinical && <Banner tone="grey">Prescriptions, medicines, vitals and documents are shown to users with the Clinical reports permission.</Banner>}
          <p className="text-xs text-gray-400">
            Only records kept in this system are shown. History from before the hospital moved to it is in the old system.
          </p>
        </div>
      )}

      {activeTab === 'visits' && (
        <ReportTable
          title="Visits"
          columns={cols.visits}
          rows={visits}
          onRowClick={openVisitPrescription}
          csvName={`${patient.mrn}-visits`}
          emptyText="No visits in this period"
          footnote={
            [
              clinical && (rxLoading ? 'Opening the prescription.' : 'Click a visit to open the prescription written on it, with its medicines.'),
              money && 'Paid is what was handed over at the desk for the token; a fee paid from the patient\'s credit is shown separately.',
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
      )}

      {activeTab === 'prescriptions' && (
        <ReportTable
          title="Prescriptions"
          columns={cols.prescriptions}
          rows={data.prescriptions || []}
          onRowClick={openPrescription}
          csvName={`${patient.mrn}-prescriptions`}
          emptyText="No prescriptions in this period"
          footnote={rxLoading ? 'Opening the prescription' : 'Click a prescription to see it in full, as it was printed.'}
        />
      )}

      {activeTab === 'medicines' && (
        <div className="space-y-5">
          <ReportTable
            title="By medicine"
            columns={cols.medicineSummary}
            rows={data.medicineSummary || []}
            csvName={`${patient.mrn}-medicines`}
            emptyText="No medicines in this period"
            footnote="Qty Given is what went out on completed bills, including supplies added at the counter. Billed is the line total before any round-off on the bill."
          />
          <ReportTable
            title="Every prescribed medicine"
            columns={cols.medicineLines}
            rows={data.medicines || []}
            csvName={`${patient.mrn}-prescribed-medicines`}
            emptyText="No medicines prescribed in this period"
          />
        </div>
      )}

      {activeTab === 'labs' && (
        <ReportTable
          title="Lab tests ordered"
          columns={cols.labs}
          rows={data.labTests || []}
          csvName={`${patient.mrn}-lab-tests`}
          emptyText="No lab tests in this period"
          footnote="A result counts once its report is uploaded to the patient's file."
        />
      )}

      {activeTab === 'vitals' && (
        <ReportTable
          title="Vitals"
          columns={cols.vitals}
          rows={data.vitals || []}
          csvName={`${patient.mrn}-vitals`}
          emptyText="No vitals recorded in this period"
          footnote="Coloured readings are outside the normal range for the patient's age at the time."
        />
      )}

      {activeTab === 'money' && breakdown && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Breakdown title="Paid by the patient" {...breakdown.paid} />
            <Breakdown title="Given by the hospital" {...breakdown.given} />
          </div>
          <ReportTable
            title="Bills"
            columns={cols.invoices}
            rows={data.invoices || []}
            onRowClick={(r) => drill.open({ type: 'invoice', value: r.number })}
            csvName={`${patient.mrn}-bills`}
            emptyText="No bills in this period"
            footnote="Click a bill to see its lines. Bills are dated by the day they were completed."
          />
          <ReportTable
            title="Items billed"
            columns={cols.billedItems}
            rows={data.billedItems || []}
            csvName={`${patient.mrn}-billed-items`}
            emptyText="No items billed in this period"
          />
          <ReportTable
            title="Credit ledger"
            columns={cols.credits}
            rows={data.credits || []}
            csvName={`${patient.mrn}-credit`}
            emptyText="The patient has never held credit"
            footnote="Money the patient paid that the hospital held for them, and how it was used. Always the whole history."
          />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-5">
          <ReportTable title="Medical alerts" columns={cols.alerts} rows={data.alerts || []} searchable={false} emptyText="No medical alerts on file" />
          <Section
            title="Medical history from the first visit"
            subtitle={
              data.intake
                ? `Completed ${fmtDateTime(data.intake.completedAt)}${data.intake.completedBy ? ` by ${data.intake.completedBy}` : ''}${
                    data.intake.amendReason ? `. Last amended: ${data.intake.amendReason}` : ''
                  }${data.intake.fromMergedRecord ? '. Taken on a record since merged into this one.' : ''}`
                : 'Not taken yet.'
            }
          >
            {data.intake && (
              <ReportTable title="Answers" columns={cols.intake} rows={data.intake.answers || []} emptyText="No answers recorded" />
            )}
          </Section>
          <ReportTable title="Report notes" columns={cols.reportNotes} rows={data.reportNotes || []} emptyText="No report notes" />
        </div>
      )}

      {activeTab === 'family' && (
        <div className="space-y-5">
          <ReportTable
            title="Family"
            columns={cols.family}
            rows={data.family || []}
            onRowClick={(r) => onOpenPatient(r.id)}
            searchable={false}
            emptyText="Not linked to a family"
            footnote={has(data.family) ? "Click a family member to open their profile." : undefined}
          />
          <ReportTable title="Guardians on file" columns={cols.guardians} rows={data.guardians || []} searchable={false} emptyText="No guardians on file" />
          <ReportTable
            title="Brought to visits by"
            columns={[
              col('label', 'Person', 'wrap'),
              col('times', 'Visits', 'number'),
              col('last', 'Last Time', 'date'),
            ]}
            rows={broughtBy}
            searchable={false}
            emptyText="No one recorded as bringing the patient"
          />
        </div>
      )}

      {activeTab === 'documents' && (
        <ReportTable
          title="Documents"
          columns={cols.documents}
          rows={data.documents || []}
          csvName={`${patient.mrn}-documents`}
          emptyText="No documents uploaded"
          footnote="Click a file name to open it. Removed documents stay listed so the record is complete."
        />
      )}

      {activeTab === 'record' && (
        <div className="space-y-5">
          <ReportTable title="Also known as" columns={cols.aliases} rows={data.aliases || []} searchable={false} emptyText="No other records were merged into this one" />
          <ReportTable title="Token edits" columns={cols.audit} rows={data.auditTrail || []} emptyText="No token was edited in this period" />
          <ReportTable title="Look-alike decisions" columns={cols.lookalikes} rows={data.lookalikes || []} searchable={false} emptyText="No look-alike decisions" />
        </div>
      )}

      <ReportPrintLayout
        title={`Patient Profile: ${name} (${patient.mrn})`}
        subtitle={`Period: ${periodText}`}
        kpis={kpis.map((k) => ({ label: k.label, value: k.value }))}
        tables={printTables}
      />

      {rx && <PrescriptionDetailModal prescription={rx} onClose={() => setRx(null)} />}
      {drill.node}
    </div>
  );
};

export default PatientProfileView;
